/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { ClosedEnum } from "../../types/enums.js";

/**
 * How the fresh registration authenticates at the issuer's token endpoint. Only with client_id; omit to keep the source's method.
 */
export const DuplicateOrganizationRemoteSessionClientFormTokenEndpointAuthMethod =
  {
    ClientSecretBasic: "client_secret_basic",
    ClientSecretPost: "client_secret_post",
    None: "none",
  } as const;
/**
 * How the fresh registration authenticates at the issuer's token endpoint. Only with client_id; omit to keep the source's method.
 */
export type DuplicateOrganizationRemoteSessionClientFormTokenEndpointAuthMethod =
  ClosedEnum<
    typeof DuplicateOrganizationRemoteSessionClientFormTokenEndpointAuthMethod
  >;

/**
 * Form for an org admin to duplicate a remote_session_client. Omit client_id to copy the source's client_id and stored secret; supply it, with the rest of a Dynamic Client Registration response, to give the copy its own upstream registration.
 */
export type DuplicateOrganizationRemoteSessionClientForm = {
  /**
   * client_id of a fresh upstream registration, e.g. from /oauth/proxy-register. Omit to copy the source's client_id and secret.
   */
  clientId?: string | undefined;
  /**
   * When the issuer reported issuing the client_id (RFC 7591 client_id_issued_at). Omit to record the time of this call.
   */
  clientIdIssuedAt?: Date | undefined;
  /**
   * client_secret of the fresh registration. Only with client_id. Speakeasy encrypts before persisting; the plaintext is never returned.
   */
  clientSecret?: string | undefined;
  /**
   * When the issuer reported the client secret expires (RFC 7591 client_secret_expires_at). Omit when the issuer reported no expiry.
   */
  clientSecretExpiresAt?: Date | undefined;
  /**
   * The remote_session_client id to duplicate.
   */
  id: string;
  /**
   * How the fresh registration authenticates at the issuer's token endpoint. Only with client_id; omit to keep the source's method.
   */
  tokenEndpointAuthMethod?:
    | DuplicateOrganizationRemoteSessionClientFormTokenEndpointAuthMethod
    | undefined;
};

/** @internal */
export const DuplicateOrganizationRemoteSessionClientFormTokenEndpointAuthMethod$outboundSchema:
  z.ZodMiniEnum<
    typeof DuplicateOrganizationRemoteSessionClientFormTokenEndpointAuthMethod
  > = z.enum(
    DuplicateOrganizationRemoteSessionClientFormTokenEndpointAuthMethod,
  );

/** @internal */
export type DuplicateOrganizationRemoteSessionClientForm$Outbound = {
  client_id?: string | undefined;
  client_id_issued_at?: string | undefined;
  client_secret?: string | undefined;
  client_secret_expires_at?: string | undefined;
  id: string;
  token_endpoint_auth_method?: string | undefined;
};

/** @internal */
export const DuplicateOrganizationRemoteSessionClientForm$outboundSchema:
  z.ZodMiniType<
    DuplicateOrganizationRemoteSessionClientForm$Outbound,
    DuplicateOrganizationRemoteSessionClientForm
  > = z.pipe(
    z.object({
      clientId: z.optional(z.string()),
      clientIdIssuedAt: z.optional(
        z.pipe(z.date(), z.transform(v => v.toISOString())),
      ),
      clientSecret: z.optional(z.string()),
      clientSecretExpiresAt: z.optional(
        z.pipe(z.date(), z.transform(v => v.toISOString())),
      ),
      id: z.string(),
      tokenEndpointAuthMethod: z.optional(
        DuplicateOrganizationRemoteSessionClientFormTokenEndpointAuthMethod$outboundSchema,
      ),
    }),
    z.transform((v) => {
      return remap$(v, {
        clientId: "client_id",
        clientIdIssuedAt: "client_id_issued_at",
        clientSecret: "client_secret",
        clientSecretExpiresAt: "client_secret_expires_at",
        tokenEndpointAuthMethod: "token_endpoint_auth_method",
      });
    }),
  );

export function duplicateOrganizationRemoteSessionClientFormToJSON(
  duplicateOrganizationRemoteSessionClientForm:
    DuplicateOrganizationRemoteSessionClientForm,
): string {
  return JSON.stringify(
    DuplicateOrganizationRemoteSessionClientForm$outboundSchema.parse(
      duplicateOrganizationRemoteSessionClientForm,
    ),
  );
}
